import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import objectId from 'bson-objectid';
import { Switch } from 'pretty-checkbox-react';
import InputMask from 'react-input-mask';

import {
  addDashboards,
  getAllDashboards,
  editDashboards,
  removeDashboards,
  selectDefaultDashboards,
  enableTransitionDashboards,
  transitionTimeDashboards,
  memberTransitionDashboards,
  setCurrentDashboard
} from '../../redux/actions/dashboards';
import AlertMsg from '../../components/AlertMsg';

const ModalConfigDash = props => {
  const dispatch = useDispatch();
  const dashboards = useSelector(state => state.dashboards);
  const authentications = useSelector(state => state.authentications);

  const INITIAL_VALUE = {
    _id: objectId().str,
    user: authentications.user._id,
    name: '',
    transition: true,
    selected: false,
    theme: 'default-theme',
    items: []
  };
  const [newDash, setNewDash] = useState({ ...INITIAL_VALUE });
  const [editDash, setEditDash] = useState({ ...INITIAL_VALUE });
  const { modalShow, setModalShow } = props;
  const dash = dashboards.data.find(dash => dash.selected);
  const totalEnabled = dashboards.data.filter(dash => dash.transition).length;
  const [editingFields, setEditingFields] = useState([]);
  const [removingFields, setRemovingFields] = useState([]);
  const addDashButton = useRef();
  const timeTransitionField = useRef();

  useEffect(() => {
    const user = authentications.user._id;
    dispatch(getAllDashboards({ user }));
  }, [dispatch, authentications.user._id]);

  const handleCloseModal = () => setModalShow(false);

  const handleExitedModal = () => {
    setEditingFields([]);
    setRemovingFields([]);
  };

  const handleClickEditField = dash => {
    setEditingFields([dash._id]);
    setEditDash(dash);
    setRemovingFields([]);
  };

  const handleClickUpdateDash = () => {
    if (editDash && editDash.name.trim()) {
      dispatch(editDashboards(editDash));
      setEditingFields([]);
    }
  };

  const handleChangeEditDash = (evt, dash) => {
    evt.preventDefault();
    const { name, value } = evt.target;
    setEditDash({ ...dash, [name]: value });
  };

  const handleClickRemoveField = id => {
    setRemovingFields([id]);
    setEditingFields([]);
  };

  const handleClickRemoveDash = id => {
    setRemovingFields([]);
    dispatch(removeDashboards(id));
  };

  const handleChangeNewDash = evt => {
    evt.preventDefault();
    const { name, value } = evt.target;
    setNewDash({ ...newDash, [name]: value });
  };

  const handleClickAddDash = () => {
    if (newDash && newDash.name.trim()) {
      const formReset = () => setNewDash({ ...INITIAL_VALUE });
      dispatch(addDashboards(newDash, formReset, addDashButton.current));
    }
  };

  const handleChangeMemberTransition = (evt, dash) => {
    const { name, checked } = evt.target;
    const updateDash = { ...dash, [name]: checked };
    if (!dashboards.isFetching && !dashboards.enableTransition) {
      dispatch(memberTransitionDashboards(updateDash));
    }
  };

  const handleClickDashDefault = dash => {
    if (!dashboards.isFetching) {
      dispatch(selectDefaultDashboards({ ...dash, selected: true }));
      if (!dashboards.enableTransition) {
        dispatch(setCurrentDashboard({ ...dash, selected: true }));
      }
    }
  };

  const handleChangeEnableTransition = () => {
    dispatch(enableTransitionDashboards(!dashboards.enableTransition));
  };

  const handleClickUpdateTimeTransition = () => {
    const hourRegex = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
    const { value } = timeTransitionField.current;
    const newValue = value.replace(/:|_/, '');
    if (hourRegex.test(value) && parseInt(newValue, 10) >= 5) {
      if (newValue !== dashboards.timeTransition) {
        dispatch(transitionTimeDashboards(newValue));
      }
    } else {
      timeTransitionField.current.value = dashboards.timeTransition;
    }
  };

  return (
    <Modal
      show={modalShow}
      onHide={handleCloseModal}
      onExited={handleExitedModal}
    >
      <Modal.Body className="pb-0">
        {dashboards.addSuccess && (
          <AlertMsg type="success" msg="Dashboard cadastrado com sucesso" />
        )}
        {dashboards.addError && (
          <AlertMsg type="danger" msg="Falha durante cadastro do dashboard" />
        )}
        {dashboards.editSuccess && (
          <AlertMsg type="success" msg="Dashboard atualizado com sucesso" />
        )}
        {dashboards.editError && (
          <AlertMsg
            type="danger"
            msg="Falha durante atualização do dashboard"
          />
        )}
        {dashboards.removeSuccess && (
          <AlertMsg type="success" msg="Dashboard removido com sucesso" />
        )}
        {dashboards.removeError && (
          <AlertMsg type="danger" msg="Falha ao remover dashboard" />
        )}
        {dashboards.selectDefaultSuccess && (
          <AlertMsg type="success" msg="Dashboard padrão selecionado" />
        )}
        {dashboards.selectDefaultError && (
          <AlertMsg type="danger" msg="Falha ao selecionar dashboard padrão" />
        )}
        {!dashboards.addSuccess &&
          !dashboards.addError &&
          !dashboards.removeSuccess &&
          !dashboards.removeError &&
          !dashboards.editSuccess &&
          !dashboards.editError &&
          !dashboards.selectDefaultSuccess &&
          !dashboards.selectDefaultError && (
          <AlertMsg type="info" msg="Área configuração de deshboards" />
        )}

        <h3 className="mb-2">
          Dashboard Padrão:{' '}
          <span className="badge badge-dark">{dash ? dash.name : ''}</span>
        </h3>
        <div className="input-group mb-3">
          <input
            type="text"
            className="form-control"
            name="name"
            placeholder="Novo Dashboard"
            autoComplete="off"
            value={newDash.name}
            onChange={handleChangeNewDash}
          />
          <div className="input-group-append">
            <button
              className="btn btn-success"
              type="button"
              onClick={handleClickAddDash}
              ref={addDashButton}
              title="Adicionar novo dasboard"
            >
              <i className="fa fa-plus" />
            </button>
          </div>
        </div>

        <div className="table-responsive table-scroll-cfg">
          <table className="table text-center table-dash-cfg  table-bordered table-striped">
            <thead>
              <tr>
                <th scope="col">Transicionar</th>
                <th scope="col">Nome</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody className="select-dash">
              {dashboards.data.map(dash => {
                const isEditing = editingFields.includes(dash._id);
                const isRemoving = removingFields.includes(dash._id);
                return (
                  <tr key={dash._id}>
                    <td>
                      <div className="checkbox" title="Desativar / Ativar">
                        <Switch
                          type="checkbox"
                          color="success"
                          name="transition"
                          onChange={evt =>
                            handleChangeMemberTransition(evt, dash)
                          }
                          checked={dash.transition}
                        />
                      </div>
                    </td>
                    <td
                      className="p-0 p-2 name-dash"
                      onClick={
                        !isEditing && !dash.selected
                          ? () => handleClickDashDefault(dash)
                          : null
                      }
                    >
                      {isEditing ? (
                        <input
                          name="name"
                          type="text"
                          autoComplete="off"
                          defaultValue={dash.name}
                          className="table-input"
                          onChange={evt => handleChangeEditDash(evt, dash)}
                        />
                      ) : dash.selected ? (
                        <span
                          className="dash-badge-select"
                          title="Dashboard atual"
                        >
                          <span className="badge badge-pill badge-success">
                            <i className="fa fa-check" />
                          </span>
                          &nbsp;
                          {dash.name}
                        </span>
                      ) : (
                        <span title="Selecionar dashboard">{dash.name}</span>
                      )}
                    </td>
                    <td className="p-0 pt-1 pb-1">
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-danger button-action rounded"
                          onClick={handleExitedModal}
                          disabled={!(isEditing || isRemoving)}
                          title="Cancelar operação"
                        >
                          <i className="fa fa-ban" />
                        </button>
                        <button
                          type="button"
                          className={`btn ${
                            isEditing ? 'btn-primary' : 'btn-warning'
                          } button-action rounded`}
                          disabled={dashboards.enableTransition}
                          onClick={() =>
                            !isEditing
                              ? handleClickEditField(dash)
                              : handleClickUpdateDash()
                          }
                          title={
                            !isEditing
                              ? 'Editar nome dasboard'
                              : 'Salvar modificação'
                          }
                        >
                          <i
                            className={`fa ${
                              isEditing ? 'fa-save' : 'fa-edit'
                            }`}
                          ></i>
                        </button>
                        <button
                          type="button"
                          disabled={
                            dashboards.data.length <= 1 ||
                            dash.selected ||
                            dashboards.enableTransition
                          }
                          onClick={() =>
                            !isRemoving
                              ? handleClickRemoveField(dash._id)
                              : handleClickRemoveDash(dash._id)
                          }
                          className={`btn ${
                            isRemoving ? 'btn-info' : 'btn-danger'
                          } button-action rounded`}
                          title={
                            !isRemoving
                              ? 'Excluir dasboard'
                              : 'Confirmar exclusão'
                          }
                        >
                          <i
                            className={`fa ${
                              isRemoving ? 'fa-check-square' : 'fa-trash'
                            }`}
                          ></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="container">
          <div className="row pt-2">
            <div className="col-xs-6 col-sm-6 col-md-6 pt-1 trans-row">
              <label>
                <i className="fas fa-exchange-alt" /> Habilitar &nbsp;
              </label>

              <div className="checkbox" title="Desativar / Ativar">
                <Switch
                  type="checkbox"
                  color="success"
                  name="enableTransition"
                  onChange={handleChangeEnableTransition}
                  checked={dashboards.enableTransition && totalEnabled > 1}
                />
              </div>
            </div>

            <div className="col-xs-6 col-sm-6 col-md-6 trans-row">
              <label className="pt-1">
                <i className="far fa-clock" /> Transição&nbsp;
              </label>
              <InputMask
                name="timeTransition"
                mask="99:99"
                className="form-control input-time"
                placeholder="MM:ss"
                defaultValue={dashboards.timeTransition}
                disabled={!dashboards.enableTransition}
                ref={timeTransitionField}
              />
              &nbsp;
              <button
                type="button"
                className="btn-update-time-trans btn-success rounded"
                title="Atualizar tempo de transição"
                disabled={!dashboards.enableTransition}
                onClick={handleClickUpdateTimeTransition}
              >
                <i className="fas fa-sync-alt" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <p>
            <small className="font-weight-bold text-danger">
              Para ativar a transição, selecione ao menos dois dashboards.
            </small>
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseModal}>
          <i className="fas fa-times" /> Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ModalConfigDash.propTypes = {
  modalShow: PropTypes.bool,
  setModalShow: PropTypes.func
};

export default ModalConfigDash;
